import { useLanguage } from '../contexts/LanguageContext';

interface AIDialogProps {
  isOpen: boolean;
  onClose: () => void;
  content: string;
  isLoading: boolean;
  error?: string;
}

export function AIDialog({ isOpen, onClose, content, isLoading, error }: AIDialogProps) {
  const { t } = useLanguage();

  if (!isOpen) return null;

  const formatContent = (text: string) => {
    // Split the content into sections
    const sections = text.split('\n\n');
    
    return sections.map((section, index) => {
      // Check if this is a heading
      if (section.startsWith('**') && section.endsWith(':**')) {
        return <h2 key={index}>{section.slice(2, -3)}</h2>;
      }
      
      // Check if this is a list with items starting with *
      if (section.split('\n').some(line => line.trim().startsWith('*'))) {
        const items = section
          .split('\n')
          .filter(line => line.trim())
          .map(line => line.trim());
        
        return (
          <ul key={index}>
            {items.map((item, itemIndex) => {
              // Process bold text within list items and remove leading asterisk
              const processedItem = item
                .replace(/^\*\s*/, '') // Remove leading asterisk and spaces
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
              return <li key={itemIndex} dangerouslySetInnerHTML={{ __html: processedItem }} />;
            })}
          </ul>
        );
      }
      
      // Check if this is the disclaimer
      if (section.includes('Disclaimer:')) {
        return <div key={index} className="disclaimer">{section}</div>;
      }
      
      // Process bold text in regular paragraphs
      const processedSection = section.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return <p key={index} dangerouslySetInnerHTML={{ __html: processedSection }} />;
    });
  };

  return (
    <div className="dialog-overlay">
      <div className="dialog">
        <div className="dialog-header">
          <h2>{t('ai.title')}</h2>
          <button className="dialog-close" onClick={onClose}>×</button>
        </div>
        <div className="dialog-content">
          {isLoading ? (
            <div className="loading">{t('ai.loading')}</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : (
            <div className="ai-response">{formatContent(content)}</div>
          )}
        </div>
      </div>
    </div>
  );
} 